/**
 * Composer.CadAnalysisModal — pick a DXF drawing + drag a rectangle to scope
 * the next query to a spatial region.
 *
 * Design goals (Ando flagship: clarity + simplicity):
 *   - One job per screen: choose a drawing (left), select an area (right).
 *   - The viewer is the existing prod ``DxfViewer`` (DRY) with its built-in
 *     ``selection`` capability — no duplicated rendering/lifecycle code.
 *   - Heavy deps (three.js + dxf-viewer + worker) are lazy-loaded only when
 *     the modal opens, so the composer's base bundle stays light.
 *   - Presentational: the file list + download URLs arrive as props, so this
 *     primitive never imports app data hooks.
 *
 * Returns the rectangle in DXF world coordinates as the wire-ready
 * ``spatial_filter`` shape (``source_file`` = filename, matching the BE
 * validator). The BE re-validates ``min < max`` on both axes.
 */

import { Crosshair, Filter, FileType2, Loader2, Sparkles } from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import { SegmentedToggle } from "../SegmentedToggle";

import type { DxfRect } from "../../cad/RegionSelectOverlay";
import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { AndoBrainLoader } from "../blob/AndoBrainLoader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";

const DxfViewerLazy = React.lazy(() =>
  import("../../cad/parsers/DxfViewer").then((m) => ({ default: m.DxfViewer })),
);

/** A selectable drawing — the app maps its DXF list into this shape. */
export interface CadModalFile {
  /** Stable key (content_item_id). */
  id: string;
  filename: string;
  /** Presigned download URL; null on failed/unavailable rows. */
  downloadUrl: string | null;
}

/** Wire-ready spatial filter (``source_file`` = filename). */
export interface CadSpatialFilterResult {
  min_x: number;
  min_y: number;
  max_x: number;
  max_y: number;
  source_file: string;
}

/** Wire-ready feature filter — Phase 5 of the CAD Big Bang (Jun 12, 2026).
 *
 * Mirrors the BE ``_validate_feature_filter`` schema 1:1 so the FE form
 * shape can be sent straight through the WS envelope as ``feature_filter``.
 * Every field is optional — the BE drops empty arrays/undefined keys, so
 * an "only material" filter on the 3D tab produces a minimal predicate. */
export interface CadFeatureFilterResult {
  feature_type?: string[];
  feature_subtype?: string[];
  diameter_mm_min?: number;
  diameter_mm_max?: number;
  material_name?: string[];
  format?: string[];
  source_file?: string;
}

export interface CadAnalysisModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: readonly CadModalFile[];
  filesLoading?: boolean;
  /** Confirm callback for the 2D spatial-rectangle path. */
  onConfirm: (filter: CadSpatialFilterResult) => void;
  /** Confirm callback for the 3D feature-filter path. When omitted the
   *  modal hides the "Modelli 3D" tab (back-compat for legacy mounts
   *  that only need spatial selection). */
  onConfirmFeature?: (filter: CadFeatureFilterResult) => void;
  /** Pre-fetched feature subtype catalog (e.g. ``["M3","M4","M6","M8"]``)
   *  so the user picks from real corpus values instead of free-form. */
  featureSubtypes?: readonly string[];
  /** Pre-fetched material catalog. */
  materials?: readonly string[];
}

// Labels resolved at render time via ``i18n._()`` so the FE Lingui
// extractor picks them up alongside the rest of the modal copy.
// The ``msg`` template carries the source string; ``i18n._()`` resolves
// to the active locale.
const FEATURE_TYPE_OPTIONS = [
  { value: "hole",    label: msg`Foro` },
  { value: "fillet",  label: msg`Raccordo` },
  { value: "chamfer", label: msg`Smusso` },
  { value: "thread",  label: msg`Filetto` },
  { value: "pocket",  label: msg`Tasca` },
  { value: "slot",    label: msg`Asola` },
  { value: "boss",    label: msg`Sporgenza` },
] as const;

type TabId = "spatial" | "feature";

export const CadAnalysisModal: React.FC<CadAnalysisModalProps> = ({
  open,
  onOpenChange,
  files,
  filesLoading = false,
  onConfirm,
  onConfirmFeature,
  featureSubtypes,
  materials,
}) => {
  const { i18n } = useI18n();
  const [activeTab, setActiveTab] = React.useState<TabId>("spatial");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [rect, setRect] = React.useState<DxfRect | null>(null);
  // Feature-filter form state (Phase 5). All optional → empty arrays
  // mean "no constraint on this column".
  const [filterTypes, setFilterTypes] = React.useState<string[]>([]);
  const [filterSubtypes, setFilterSubtypes] = React.useState<string[]>([]);
  const [filterMaterials, setFilterMaterials] = React.useState<string[]>([]);
  const [filterDiameterMin, setFilterDiameterMin] = React.useState<string>("");
  const [filterDiameterMax, setFilterDiameterMax] = React.useState<string>("");

  const selected = React.useMemo(
    () => files.find((f) => f.id === selectedId) ?? null,
    [files, selectedId],
  );

  // Reset transient state whenever the modal closes.
  React.useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setRect(null);
      setActiveTab("spatial");
      setFilterTypes([]);
      setFilterSubtypes([]);
      setFilterMaterials([]);
      setFilterDiameterMin("");
      setFilterDiameterMax("");
    }
  }, [open]);

  // Selecting a drawing also clears any pending selection (inline so there's
  // no effect with a trigger-only dependency).
  // Accepts ``null`` so the feature-filter panel can clear the scope
  // ("Tutti i file del corpus") without leaking an empty string into the
  // wire payload. ``""`` would resolve to a falsy file lookup but still
  // ride downstream as ``source_file: ""`` in the BE feature_filter dict.
  const selectDrawing = React.useCallback((id: string | null) => {
    setSelectedId(id);
    setRect(null);
  }, []);

  const confirm = React.useCallback(() => {
    if (!rect || !selected) return;
    onConfirm({
      min_x: rect.minX,
      min_y: rect.minY,
      max_x: rect.maxX,
      max_y: rect.maxY,
      source_file: selected.filename,
    });
    onOpenChange(false);
  }, [rect, selected, onConfirm, onOpenChange]);

  const featureFilterIsActionable =
    filterTypes.length > 0
    || filterSubtypes.length > 0
    || filterMaterials.length > 0
    || filterDiameterMin.trim() !== ""
    || filterDiameterMax.trim() !== "";

  // Parse + sanitise the diameter range BEFORE checking ``actionable``
  // so the footer button reflects validity (a user that types
  // ``min=20, max=5`` sees the confirm disabled with an explainer).
  const parsedDiameter = React.useMemo(() => {
    const parseMm = (raw: string): number | null => {
      if (!raw.trim()) return null;
      const norm = raw.replace(/\./g, "").replace(",", ".");
      const v = Number.parseFloat(norm);
      if (!Number.isFinite(v) || v < 0 || v > 1e5) return null;
      return v;
    };
    const min = parseMm(filterDiameterMin);
    const max = parseMm(filterDiameterMax);
    const invalid =
      (filterDiameterMin.trim() !== "" && min === null)
      || (filterDiameterMax.trim() !== "" && max === null)
      || (min !== null && max !== null && min > max);
    return { min, max, invalid };
  }, [filterDiameterMin, filterDiameterMax]);

  const confirmFeature = React.useCallback(() => {
    if (!onConfirmFeature || !featureFilterIsActionable) return;
    if (parsedDiameter.invalid) return;
    const payload: CadFeatureFilterResult = {};
    if (filterTypes.length) payload.feature_type = filterTypes;
    if (filterSubtypes.length) payload.feature_subtype = filterSubtypes;
    if (filterMaterials.length) payload.material_name = filterMaterials;
    if (parsedDiameter.min !== null) payload.diameter_mm_min = parsedDiameter.min;
    if (parsedDiameter.max !== null) payload.diameter_mm_max = parsedDiameter.max;
    if (selected) payload.source_file = selected.filename;
    onConfirmFeature(payload);
    onOpenChange(false);
  }, [
    onConfirmFeature, featureFilterIsActionable, parsedDiameter,
    filterTypes, filterSubtypes, filterMaterials,
    selected, onOpenChange,
  ]);

  const toggleInArray = React.useCallback(
    (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) => {
      setter((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
      );
    },
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-hairline border-b px-5 py-3.5">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Crosshair size={16} className="text-ando-orange" aria-hidden />
            <Trans>Analisi CAD</Trans>
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {activeTab === "spatial" ? (
              <Trans>Seleziona un disegno, attiva la selezione e trascina un'area da analizzare.</Trans>
            ) : (
              <Trans>Filtra il corpus per feature, materiale e diametro — la risposta arriva da dati strutturati.</Trans>
            )}
          </DialogDescription>

          {/* Tab switch — visible only when the 3D feature path is wired
              up by the host. Reuses the shared ``SegmentedToggle`` (Radix-
              free wrapper used elsewhere in the app, e.g. ``SubTabBar``)
              so we get keyboard nav + ``aria-pressed`` semantics + the
              canonical bg-ink-1/[0.04] visual treatment for free
              (DRY win identified by the FE audit). */}
          {onConfirmFeature ? (
            <SegmentedToggle
              value={activeTab}
              onChange={setActiveTab}
              ariaLabel={i18n._(msg`Modalità Analisi CAD`)}
              className="mt-2"
              options={[
                { id: "spatial", label: i18n._(msg`Area 2D`), icon: <Crosshair size={11} aria-hidden /> },
                { id: "feature", label: i18n._(msg`Filtro feature`), icon: <Filter size={11} aria-hidden /> },
              ]}
            />
          ) : null}
        </DialogHeader>

        {activeTab === "feature" ? (
          <FeatureFilterPanel
            files={files}
            selectedFile={selected}
            onSelectFile={selectDrawing}
            filterTypes={filterTypes}
            setFilterTypes={setFilterTypes}
            filterSubtypes={filterSubtypes}
            toggleSubtype={(v) => toggleInArray(setFilterSubtypes, v)}
            filterMaterials={filterMaterials}
            toggleMaterial={(v) => toggleInArray(setFilterMaterials, v)}
            filterDiameterMin={filterDiameterMin}
            setFilterDiameterMin={setFilterDiameterMin}
            filterDiameterMax={filterDiameterMax}
            setFilterDiameterMax={setFilterDiameterMax}
            featureSubtypes={featureSubtypes ?? []}
            materials={materials ?? []}
          />
        ) : null}

        <div
          className={cn(
            "flex min-h-0 flex-1",
            activeTab === "feature" && "hidden",
          )}
        >
          {/* Left — drawing list */}
          <div className="flex w-52 shrink-0 flex-col border-hairline border-r bg-cream/30">
            <div className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
              <Trans>Disegni DXF</Trans>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {filesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={16} className="animate-spin text-ink-3" aria-label={i18n._(msg`Caricamento`)} />
                </div>
              ) : files.length === 0 ? (
                <div className="px-3 py-6 text-center">
                  <FileType2 size={22} className="mx-auto mb-2 text-ink-3" aria-hidden />
                  <p className="text-[11px] text-ink-3"><Trans>Nessun disegno DXF trovato</Trans></p>
                </div>
              ) : (
                files.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => selectDrawing(f.id)}
                    aria-pressed={selectedId === f.id}
                    title={f.filename}
                    className={cn(
                      "block w-full truncate px-3 py-2 text-left text-[12px] transition-colors",
                      selectedId === f.id
                        ? "border-ando-orange border-l-2 bg-ando-orange-soft/50 font-medium text-ink-1"
                        : "border-transparent border-l-2 text-ink-2 hover:bg-ink-1/[0.04]",
                    )}
                  >
                    {f.filename}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right — viewer + region select */}
          <div className="relative min-w-0 flex-1 bg-ink-1">
            {!selected ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <Crosshair size={34} className="text-cream/30" aria-hidden />
                <p className="text-[13px] text-cream/60"><Trans>Seleziona un disegno dalla lista</Trans></p>
              </div>
            ) : !selected.downloadUrl ? (
              <div className="flex h-full items-center justify-center px-6 text-center">
                <p className="text-[13px] text-cream/60">
                  <Trans>Anteprima non disponibile per questo disegno.</Trans>
                </p>
              </div>
            ) : (
              <React.Suspense
                fallback={
                  <output
                    aria-busy
                    className="flex h-full items-center justify-center"
                    aria-label={i18n._(msg`Caricamento viewer`)}
                  >
                    <AndoBrainLoader size="md" />
                  </output>
                }
              >
                <DxfViewerLazy
                  // Jun 13, 2026 (UI audit M7) — no ``key={selected.id}``
                  // remount: ``DxfViewer`` already reacts to ``fileUrl``
                  // changes via its own ``useEffect`` so flipping files
                  // doesn't tear down the three.js context (~150-400ms
                  // saved per switch).
                  fileUrl={selected.downloadUrl}
                  filename={selected.filename}
                  ariaLabel={i18n._(msg`Disegno ${selected.filename}`)}
                  selection={{ onSelect: setRect }}
                />
              </React.Suspense>
            )}
          </div>
        </div>

        {/* Footer — pivot per active tab */}
        <div className="flex items-center justify-between gap-3 border-hairline border-t px-5 py-3">
          <span
            className={cn(
              "min-w-0 flex-1 truncate font-mono text-[11px] tabular-nums",
              activeTab === "feature" && parsedDiameter.invalid
                ? "text-red-600"
                : "text-ink-3",
            )}
          >
            {activeTab === "spatial"
              ? rect
                ? `X: ${rect.minX.toFixed(0)} .. ${rect.maxX.toFixed(0)}  ·  Y: ${rect.minY.toFixed(0)} .. ${rect.maxY.toFixed(0)}`
                : i18n._(msg`Nessuna area selezionata`)
              : parsedDiameter.invalid
                ? i18n._(msg`Range diametro non valido (min ≤ max, ≥ 0)`)
                : featureFilterIsActionable
                  ? i18n._(msg`Filtro pronto`)
                  : i18n._(msg`Nessun filtro attivo`)}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-hairline px-3 py-1.5 text-[12px] text-ink-2 transition-colors hover:bg-ink-1/[0.04]"
            >
              <Trans>Annulla</Trans>
            </button>
            {activeTab === "spatial" ? (
              <button
                type="button"
                onClick={confirm}
                disabled={!rect || !selected}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 font-medium text-[12px] text-white transition-colors",
                  "bg-ando-orange hover:bg-ando-orange/90",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
                )}
              >
                <Crosshair size={13} aria-hidden />
                <Trans>Analizza area</Trans>
              </button>
            ) : (
              <button
                type="button"
                onClick={confirmFeature}
                disabled={!featureFilterIsActionable || parsedDiameter.invalid}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 font-medium text-[12px] text-white transition-colors",
                  "bg-ando-orange hover:bg-ando-orange/90",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
                )}
              >
                <Sparkles size={13} aria-hidden />
                <Trans>Applica filtro</Trans>
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
CadAnalysisModal.displayName = "Composer.CadAnalysisModal";


// ---------------------------------------------------------------------------
// FeatureFilterPanel — Phase 5 of the CAD Big Bang (Jun 12, 2026 SOTA).
//
// Multi-select form that mirrors the BE ``feature_filter`` schema. Layout
// uses the same 2-column footprint as the spatial tab — file list on the
// left, form on the right — so the user's mental model stays consistent
// when they switch tabs.
// ---------------------------------------------------------------------------

interface FeatureFilterPanelProps {
  files: readonly CadModalFile[];
  selectedFile: CadModalFile | null;
  /** ``null`` clears the current selection — corpus-wide scope. */
  onSelectFile: (id: string | null) => void;
  filterTypes: string[];
  setFilterTypes: React.Dispatch<React.SetStateAction<string[]>>;
  filterSubtypes: string[];
  toggleSubtype: (v: string) => void;
  filterMaterials: string[];
  toggleMaterial: (v: string) => void;
  filterDiameterMin: string;
  setFilterDiameterMin: (v: string) => void;
  filterDiameterMax: string;
  setFilterDiameterMax: (v: string) => void;
  featureSubtypes: readonly string[];
  materials: readonly string[];
}

const FeatureFilterPanel: React.FC<FeatureFilterPanelProps> = ({
  files,
  selectedFile,
  onSelectFile,
  filterTypes,
  setFilterTypes,
  filterSubtypes,
  toggleSubtype,
  filterMaterials,
  toggleMaterial,
  filterDiameterMin,
  setFilterDiameterMin,
  filterDiameterMax,
  setFilterDiameterMax,
  featureSubtypes,
  materials,
}) => {
  const { i18n } = useI18n();
  return (
    <div className="flex min-h-0 flex-1">
      {/* Left — file scope (optional). Empty selection = corpus-wide. */}
      <div className="flex w-52 shrink-0 flex-col border-hairline border-r bg-cream/30">
        <div className="px-3 py-2 font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
          <Trans>Disegno (opzionale)</Trans>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <button
            type="button"
            onClick={() => onSelectFile(null)}
            aria-pressed={!selectedFile}
            className={cn(
              "block w-full truncate px-3 py-2 text-left text-[12px] transition-colors",
              !selectedFile
                ? "border-ando-orange border-l-2 bg-ando-orange-soft/50 font-medium text-ink-1"
                : "border-transparent border-l-2 text-ink-2 hover:bg-ink-1/[0.04]",
            )}
          >
            <Trans>Tutti i file del corpus</Trans>
          </button>
          {files.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onSelectFile(f.id)}
              aria-pressed={selectedFile?.id === f.id}
              title={f.filename}
              className={cn(
                "block w-full truncate px-3 py-2 text-left text-[12px] transition-colors",
                selectedFile?.id === f.id
                  ? "border-ando-orange border-l-2 bg-ando-orange-soft/50 font-medium text-ink-1"
                  : "border-transparent border-l-2 text-ink-2 hover:bg-ink-1/[0.04]",
              )}
            >
              {f.filename}
            </button>
          ))}
        </div>
      </div>

      {/* Right — multi-select form */}
      <div className="min-w-0 flex-1 overflow-y-auto bg-cream/20 px-5 py-4">
        <div className="mx-auto flex max-w-2xl flex-col gap-5">
          <FilterSection
            label={<Trans>Tipo di feature</Trans>}
            help={<Trans>Categorie geometriche estratte dall'analizzatore SOTA.</Trans>}
          >
            <div className="flex flex-wrap gap-1.5">
              {FEATURE_TYPE_OPTIONS.map((opt) => {
                const active = filterTypes.includes(opt.value);
                return (
                  <ChipToggle
                    key={opt.value}
                    active={active}
                    onClick={() =>
                      setFilterTypes((prev) =>
                        prev.includes(opt.value)
                          ? prev.filter((v) => v !== opt.value)
                          : [...prev, opt.value],
                      )
                    }
                  >
                    {i18n._(opt.label)}
                  </ChipToggle>
                );
              })}
            </div>
          </FilterSection>

          {featureSubtypes.length > 0 ? (
            <FilterSection
              label={<Trans>Sottotipo / filettatura</Trans>}
              help={<Trans>Catalogo del corpus — esempio: M6, M8, H7.</Trans>}
            >
              <div className="flex flex-wrap gap-1.5">
                {featureSubtypes.map((sub) => (
                  <ChipToggle
                    key={sub}
                    active={filterSubtypes.includes(sub)}
                    onClick={() => toggleSubtype(sub)}
                  >
                    {sub}
                  </ChipToggle>
                ))}
              </div>
            </FilterSection>
          ) : null}

          <FilterSection
            label={<Trans>Diametro (mm)</Trans>}
            help={<Trans>Intervallo di diametro applicato alle feature estratte.</Trans>}
          >
            <div className="flex items-center gap-2 text-[12px]">
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                value={filterDiameterMin}
                onChange={(e) => setFilterDiameterMin(e.target.value)}
                placeholder="min"
                aria-label={i18n._(msg`Diametro minimo in millimetri`)}
                className="w-24 rounded-md border border-hairline bg-cream/60 px-2 py-1 text-right text-ink-1 outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
              />
              <span className="text-ink-3" aria-hidden>→</span>
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9.,]*"
                value={filterDiameterMax}
                onChange={(e) => setFilterDiameterMax(e.target.value)}
                placeholder="max"
                aria-label={i18n._(msg`Diametro massimo in millimetri`)}
                className="w-24 rounded-md border border-hairline bg-cream/60 px-2 py-1 text-right text-ink-1 outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
              />
              <span className="text-[11px] text-ink-3">mm</span>
            </div>
          </FilterSection>

          {materials.length > 0 ? (
            <FilterSection
              label={<Trans>Materiale</Trans>}
              help={<Trans>Materiali estratti dal title block 2D / XCAFDoc 3D.</Trans>}
            >
              <div className="flex flex-wrap gap-1.5">
                {materials.map((m) => (
                  <ChipToggle
                    key={m}
                    active={filterMaterials.includes(m)}
                    onClick={() => toggleMaterial(m)}
                  >
                    {m}
                  </ChipToggle>
                ))}
              </div>
            </FilterSection>
          ) : null}
        </div>
      </div>
    </div>
  );
};
FeatureFilterPanel.displayName = "Composer.CadAnalysisModal.FeatureFilterPanel";


const FilterSection: React.FC<{
  label: React.ReactNode;
  help?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, help, children }) => (
  <section className="flex flex-col gap-2">
    <div className="flex flex-col">
      <span className="font-semibold text-[11px] text-ink-2 uppercase tracking-wide">
        {label}
      </span>
      {help ? (
        <span className="text-[11px] text-ink-3">{help}</span>
      ) : null}
    </div>
    {children}
  </section>
);

const ChipToggle: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center rounded-full border px-2.5 py-1 text-[12px] transition-colors",
      active
        ? "border-ando-orange bg-ando-orange-soft font-medium text-ando-orange"
        : "border-hairline bg-cream/40 text-ink-2 hover:bg-ink-1/[0.04]",
    )}
  >
    {children}
  </button>
);
